import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import ImageCard from "@/components/ImageCard";
import Lightbox from "@/components/Lightbox";
import { Input } from "@/components/ui/input";
import { Search, Images, Loader2 } from "lucide-react";

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await api.get("/images/gallery", { params: query ? { q: query } : {} });
      setImages(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Public Gallery</h1>
          <p className="text-sm text-muted-foreground mt-1">Images shared by the community.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="gallery-search"
            placeholder="Search images…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : images.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-28 text-center" data-testid="gallery-empty">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <Images className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="font-heading font-semibold text-lg">Nothing here yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            Be the first to share — upload an image, sign in, and toggle it public from your history.
          </p>
        </div>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4" data-testid="gallery-grid">
          {images.map((img) => (
            <ImageCard key={img.id} image={img} onClick={setSelected} />
          ))}
        </div>
      )}

      <Lightbox image={selected} onOpenChange={setSelected} />
    </div>
  );
}
